<div class="deck" [class.presenting]="presenting()">
  <!-- A thumbnail is the page alone: the list it sits in has the name, the actions and the paging. -->
  @if (!thumbnail) {
  <!-- Top bar. This subsection has no Navigator chrome, so it supplies its own. -->
  <header class="deck-top d-flex align-items-center px-4 py-2">
    <!-- The logo travels with the name (NK-518): one block, so the top bar wraps the buttons onto a
         line of their own before it ever separates the two. -->
    <div class="deck-top-title d-flex align-items-center">
    @if (logoUrl(); as logo) {
      <img class="deck-logo mr-3" [src]="logo" alt="Logo">
    }
    <div class="deck-top-name">
      @if (editing()) {
        <input type="text"
               class="inline-edit deck-name-input nav-font18"
               aria-label="Report name"
               [ngModel]="editedName()"
               (ngModelChange)="editedName.set($event)">
        <input type="text"
               class="inline-edit deck-top-meta nav-font12"
               aria-label="Report description"
               placeholder="Add a description"
               [ngModel]="editedDescription()"
               (ngModelChange)="editedDescription.set($event)">
      }
      @else {
        <h1 class="nav-font18 mb-0">{{ deckName() }}</h1>
        <!-- In present mode too: it is what the deck is about, and the room it was saving there has
             since come back with the back link moving to the foot of the index. -->
        @if (deckDescription()) {
          <p class="nav-font12 deck-top-meta mb-0">{{ deckDescription() }}</p>
        }
      }
    </div>
    </div>

    <div class="deck-top-actions ml-auto d-flex align-items-center">
      <!-- Everything from here to the Share button is Navigator's own: editing, refreshing, history,
           the data source. A share link renders none of it — see REPORT_VIEW_MODE, which defaults to
           the client, so anything added inside here is hidden from them unless it says otherwise. -->
      <ng-container *internalOnly>
      @if (!presenting()) {
        <!-- Two menus rather than seven buttons: the bar ran out of room once the data source joined
             it. Grouped by what they do to the deck -- Edit changes its content, Actions acts on it
             as a whole -- and while editing the exit stays a plain button, since hunting through a
             menu to stop editing is worse than the crowding this fixes. -->
        <!-- The report-wide settings, shown viewing and editing alike so they are always where people
             look for them. Outside edit mode they switch into it first: every change goes into the
             edit's draft, with Save and Cancel, and never straight onto the live report. -->
        <!-- The report's colours, behind one button. The panel floats under it rather than pushing
             the deck down, and closes only from its button, its × or Esc: the colour pickers open
             on <body>, and closing on a click outside would take the first click in one as leaving
             the panel. -->
          <div class="theme-control mr-2" (keydown.escape)="closeThemePanel()">
          <button #themeToggle
                  type="button"
                  class="theme-toggle btn btn-sm deck-btn is-tool shadow-none d-flex align-items-center"
                  aria-controls="deck-theme-panel"
                  [attr.aria-expanded]="themePanelOpen()"
                  [disabled]="savingDesign()"
                  title="Report colours"
                  (click)="openTheme()">
            <i class="fas fa-palette mr-1" aria-hidden="true"></i><span class="deck-btn-label">Theme</span>
            <span class="theme-dots ml-2" aria-hidden="true">
              @for (entry of paletteRoles; track entry.role) {
                <span class="theme-dot" [style.background-color]="palette()[entry.role]"></span>
              }
            </span>
            @if (coloursCustomised()) {
              <span class="theme-custom-tag ml-2">Custom</span>
            }
            <i class="fas fa-chevron-down theme-chevron ml-auto" [class.is-open]="themePanelOpen()" aria-hidden="true"></i>
          </button>
          @if (themePanelOpen()) {
          <div id="deck-theme-panel" class="theme-panel" role="group" aria-label="Report colours"
               [style.top.px]="themePanelPosition().top"
               [style.left.px]="themePanelPosition().left">
            <div class="d-flex align-items-center mb-2">
              <span class="theme-panel-title">Report colours</span>
              <button type="button" class="btn btn-sm p-0 ml-auto theme-panel-close"
                      aria-label="Close report colours"
                      (click)="closeThemePanel()">
                <i class="fas fa-close" aria-hidden="true"></i>
              </button>
            </div>
          <!-- NK-450: a theme sets every colour below at once. Changing one by hand afterwards
               changes this report only -- never the theme -- and it then says so, with the way
               back. -->
          <!-- One row per setting, labelled down the same column as Colours and Text. The picker's
               own swatches are off: the Colours row under it shows them. -->
          <div class="palette-theme mb-2">
            <div class="palette-strip d-flex align-items-center">
              <span class="palette-strip-label grey4 nav-font12 mr-2" aria-hidden="true">Theme</span>
              <or-theme-picker class="flex-fill"
                             inputId="deck-theme"
                             label="Theme"
                             [inline]="true"
                             [showSwatches]="false"
                             [fill]="true"
                             [themes]="themes()"
                             [themeId]="currentThemeId()"
                             [outOfDate]="themeOutOfDate()"
                             [disabled]="savingDesign()"
                             (picked)="pickTheme($event)"
                             (updateRequested)="updateToLatestTheme()"></or-theme-picker>
            </div>
            @if (coloursCustomised()) {
              <div class="d-flex align-items-center mt-1">
                <span class="palette-custom nav-font12">
                  <i class="fas fa-pen-nib mr-1" aria-hidden="true"></i>Custom colours
                </span>
                <button type="button" class="btn btn-sm btn-link px-0 shadow-none nav-font12 ml-auto"
                        [disabled]="savingDesign()"
                        (click)="revertToTheme()">
                  Revert to theme
                </button>
              </div>
            }
          </div>
          <!-- Every chart colour and most of the deck's chrome resolves from these five, so this is
               the one control that repaints the whole report. -->
          @for (group of paletteGroups; track group.label) {
            <div class="palette-strip d-flex align-items-center mb-2" role="group" [attr.aria-label]="group.label">
              <span class="palette-strip-label grey4 nav-font12 mr-2" aria-hidden="true">{{ group.label }}</span>
              @for (entry of group.roles; track entry.role) {
                <span ngx-colors-trigger
                      class="palette-dot mr-1"
                      role="button"
                      tabindex="0"
                      format="hex"
                      acceptLabel="Select"
                      cancelLabel="Cancel"
                      [attr.aria-label]="entry.label + ' colour'"
                      [title]="entry.label"
                      [style.background-color]="colourOf(entry.role)"
                      [ngModel]="colourOf(entry.role)"
                      (ngModelChange)="setPaletteColor(entry.role, $event)"></span>
              }
            </div>
          }
            <!-- NK-518: the logo comes with the theme, and can be swapped for one of this deck's own
                 -- an edit like a hand-picked colour, which a theme update puts back. Hiding it is
                 this deck's choice alone, and stays put through theme updates. -->
            <div class="palette-strip d-flex align-items-start mb-2" role="group" aria-label="Logo">
              <span class="palette-strip-label grey4 nav-font12 mr-2 mt-1" aria-hidden="true">Logo</span>
              <div class="flex-fill">
                <div class="d-flex align-items-center">
                  @if (logoUrl(); as logo) {
                    <img class="theme-logo-thumb mr-2" [src]="logo" alt="Report logo">
                  } @else if (design()?.logo) {
                    <span class="grey4 nav-font12 mr-2">Hidden</span>
                  } @else {
                    <span class="grey4 nav-font12 mr-2">None</span>
                  }
                  <label class="btn btn-sm btn-link p-0 nav-font12 mb-0 ml-auto"
                         [class.disabled]="uploadingLogo() || savingDesign()">
                    @if (uploadingLogo()) {
                      <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
                    }
                    {{ design()?.logo ? 'Replace' : 'Upload' }}
                    <input #reportLogoInput type="file" class="sr-only"
                           accept="image/png,image/jpeg,image/gif,image/webp"
                           [disabled]="uploadingLogo() || savingDesign()"
                           (change)="uploadReportLogo(reportLogoInput)">
                  </label>
                </div>
                @if (design()?.logo) {
                  <div class="custom-control custom-checkbox mt-1">
                    <input type="checkbox"
                           class="custom-control-input"
                           id="deck-hide-logo"
                           [checked]="!!design()?.hideLogo"
                           [disabled]="savingDesign()"
                           (change)="setHideLogo($any($event.target).checked)">
                    <label class="custom-control-label nav-font12" for="deck-hide-logo">Hide logo on this report</label>
                  </div>
                }
                @if (logoCustomised()) {
                  <div class="d-flex align-items-center mt-1">
                    <span class="palette-custom nav-font12">
                      <i class="fas fa-pen-nib mr-1" aria-hidden="true"></i>Custom logo
                    </span>
                    <button type="button" class="btn btn-sm btn-link px-0 shadow-none nav-font12 ml-auto"
                            [disabled]="savingDesign()"
                            (click)="useThemeLogo()">
                      Use theme's logo
                    </button>
                  </div>
                }
                @if (logoError(); as error) {
                  <p class="text-danger nav-font12 mt-1 mb-0" role="alert">{{ error }}</p>
                }
              </div>
            </div>
            <!-- While viewing, the colours are a preview until saved here. While editing, Save
                 changes in the bar keeps them with everything else. -->
            @if (!editing()) {
              <div class="theme-panel-foot d-flex align-items-center">
                <button type="button" class="btn btn-sm btn-link p-0 nav-font12 ml-auto mr-3"
                        [disabled]="savingDesign()"
                        (click)="closeThemePanel()">Cancel</button>
                <button type="button" class="btn btn-sm btn-primary"
                        [disabled]="savingDesign() || !unsaved()"
                        (click)="saveThemePanel()">{{ savingDesign() ? 'Saving…' : 'Save' }}</button>
              </div>
            }
          </div>
          }
          </div>
          <!-- The report's statistics: one dialog, also opened from a crosstab's settings. The
               summary says what is set without opening it. -->
          <button type="button"
                  class="stats-toggle btn btn-sm deck-btn is-tool shadow-none d-flex align-items-center mr-3"
                  aria-haspopup="dialog"
                  [disabled]="savingDesign()"
                  [title]="'Report statistics: ' + statisticsSummary() "
                  (click)="showStatistics()">
            <i class="fas fa-calculator mr-1" aria-hidden="true"></i><span class="deck-btn-label">Statistics</span>
            <span class="stats-toggle-summary ml-2 text-truncate">{{ statisticsSummary() }}</span>
          </button>
        @if (!isTemplate() && !editing()) {
          <div class="btn-group mr-2" dropdown>
            <button dropdownToggle type="button" class="btn btn-sm deck-btn is-tool shadow-none dropdown-toggle"
                    [disabled]="savingDesign()"
                    title="Edit"
                    aria-controls="deck-edit-menu">
              <i class="fas mr-1" [class.fa-pen]="!savingDesign()" [class.fa-spinner]="savingDesign()"
                 [class.fa-spin]="savingDesign()" aria-hidden="true"></i><span class="deck-btn-label">{{ savingDesign() ? 'Saving…' : 'Edit' }}</span>
            </button>
            <ul id="deck-edit-menu" *dropdownMenu class="dropdown-menu dropdown-menu-right" role="menu">
              <li role="menuitem">
                <button type="button" class="dropdown-item" (click)="toggleEditing()">
                  <i class="fas fa-pen mr-2" aria-hidden="true"></i>Manual
                </button>
              </li>
              <!-- Only while the chat is a pill: all it does is open the window, which is otherwise
                   already on the deck. -->
              @if (chatMinimized()) {
                <li role="menuitem">
                  <button type="button" class="dropdown-item" (click)="openChat()">
                  <i class="fas fa-wand-magic-sparkles mr-2" aria-hidden="true"></i>AI Assisted
                </button>
              </li>
              }
              <li role="menuitem">
                <button type="button" class="dropdown-item"
                        [disabled]="refreshing() || switchingSource()"
                        (click)="chooseDataSource()">
                  <i class="fas fa-database mr-2" aria-hidden="true"></i>{{ switchingSource() ? 'Switching…' : 'Change data source' }}
                  @if (dataSourceName()) {
                    <span class="d-block grey4 nav-font12">Currently {{ dataSourceName() }}</span>
                  }
                </button>
              </li>
            </ul>
          </div>

          <div class="btn-group mr-2" dropdown>
            <button dropdownToggle type="button" class="btn btn-sm deck-btn is-tool shadow-none dropdown-toggle"
                    title="Actions"
                    aria-controls="deck-actions-menu">
              <i class="fas fa-ellipsis mr-1" [class.fa-spin]="refreshing()" aria-hidden="true"></i><span class="deck-btn-label">Actions</span>
            </button>
            <ul id="deck-actions-menu" *dropdownMenu class="dropdown-menu dropdown-menu-right" role="menu">
              @if (!isTemplate()) {
                <li role="menuitem">
                  <button type="button" class="dropdown-item" (click)="share()">
                    <i class="fas fa-share-nodes mr-2" aria-hidden="true"></i>Share
                  </button>
                </li>
              }
              @if (canSeeHistory) {
                <li role="menuitem">
                  <button type="button" class="dropdown-item" (click)="openHistory()">
                    <i class="fas fa-clock-rotate-left mr-2" aria-hidden="true"></i>History
                  </button>
                </li>
              }
              <li role="menuitem">
                <button type="button" class="dropdown-item"
                        [disabled]="savingTemplate()"
                        (click)="saveAsTemplate()">
                  <i class="fas mr-2" [class.fa-bookmark]="!savingTemplate()" [class.fa-spinner]="savingTemplate()"
                     [class.fa-spin]="savingTemplate()" aria-hidden="true"></i>{{ savingTemplate() ? 'Saving…' : 'Save as template' }}
                </button>
              </li>
              <li role="menuitem">
                <button type="button" class="dropdown-item"
                        [disabled]="refreshing() || switchingSource()"
                        (click)="refreshData()">
                  <i class="fas fa-rotate-right mr-2" [class.fa-spin]="refreshing()" aria-hidden="true"></i>{{ refreshing() ? 'Refreshing…' : 'Refresh data' }}
                  @if (dataAsOfLabel()) {
                    <span class="d-block grey4 nav-font12">{{ dataAsOfLabel() }}</span>
                  }
                </button>
              </li>
              <li role="menuitem">
                <button type="button" class="dropdown-item"
                        [disabled]="!hasRecords() || !dataDownloads().length"
                        (click)="openDataDownload()">
                  <i class="fas fa-download mr-2" aria-hidden="true"></i>Download data
                  @if (!dataDownloads().length) {
                    <span class="d-block grey4 nav-font12">{{ noDownloadReason() }}</span>
                  }
                </button>
              </li>
            </ul>
          </div>
        } @else if (isTemplate() && !editing()) {
          <!-- A template's way into edit mode, and its history (NK-450). The deck's menus above are
               decks only, so without these a template had Save and Cancel and nothing to edit. -->
          <button type="button" class="btn btn-sm deck-btn shadow-none mr-2"
                  [disabled]="savingDesign()"
                  title="Edit"
                  (click)="toggleEditing()">
            <i class="fas fa-pen mr-1" aria-hidden="true"></i><span class="deck-btn-label">Edit</span>
          </button>
          @if (canSeeHistory) {
            <button type="button" class="btn btn-sm deck-btn shadow-none mr-2"
                    title="History"
                    (click)="openHistory()">
              <i class="fas fa-clock-rotate-left mr-1" aria-hidden="true"></i><span class="deck-btn-label">History</span>
            </button>
          }
        } @else {
          <button type="button" class="btn btn-sm deck-btn is-tool shadow-none mr-2"
                  [disabled]="savingDesign() || !unsaved()"
                  title="Save changes"
                  (click)="saveDesign()">
            <i class="fas fa-save mr-1" aria-hidden="true"></i><span class="deck-btn-label">{{ savingDesign() ? 'Saving…' : 'Save changes' }}</span>
          </button>
          <button type="button" class="btn btn-sm deck-btn is-tool shadow-none mr-2"
                  [class.is-confirming]="confirmingCancel()"
                  [disabled]="savingDesign()"
                  title="Cancel"
                  (click)="cancelEditing()">
            <i class="fas fa-rotate-left mr-1" aria-hidden="true"></i><span class="deck-btn-label">{{ unsaved() ? (confirmingCancel() ? 'Discard changes?' : 'Cancel') : 'Cancel' }}</span>
          </button>
        }

        }
      </ng-container>
        <!-- Decks only: a template has no survey data to present. While presenting, the button's
             other state: where you are, and the way out. Not while editing, which has to be
             finished first -- the button would only ever be disabled there. -->
        @if (!isTemplate()) {
          @if (presenting()) {
            <span class="nav-font12 mr-3 deck-top-meta">{{ subSectionIndex() + 1 }} / {{ subSectionCount() }}</span>
            <button type="button" class="btn btn-sm deck-btn is-solid shadow-none"
                    title="Exit presenting"
                    (click)="togglePresent()">
              <i class="fas fa-close mr-1" aria-hidden="true"></i><span class="deck-btn-label">Exit presenting</span>
            </button>
          } @else if (!editing()) {
            <button type="button" class="btn btn-sm deck-btn is-solid shadow-none"
                    title="Present"
                    (click)="togglePresent()">
              <i class="fas fa-play mr-1" aria-hidden="true"></i><span class="deck-btn-label">Present</span>
            </button>
          }
        }
    </div>
  </header>

  <!-- Every banner in here reports on something a Navigator user just did -- a save, a template, the
       assistant. None of it means anything to a client, and some of it says more about how the deck
       is made than they should be reading.

       Which of them can be closed follows what they report. A one-off -- a confirmation, a failure
       nobody is asked to act on -- has an X, and a confirmation also goes by itself after a few
       seconds. A state still true -- unsaved changes, a save in flight, a reload needed -- has none:
       it goes when the state does, and closing it would only hide something still the case. -->
  <ng-container *internalOnly>
  @if (nameSaveFailed()) {
    <div class="unsaved-banner has-close nav-font12 px-4 py-2">
      <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
      Couldn't save the {{ isTemplate() ? 'template' : 'report' }} name.
      <button type="button" class="banner-close btn btn-sm p-0" aria-label="Dismiss" (click)="nameSaveFailed.set(false)">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  }

  @if (templateSaved(); as savedName) {
    <div class="unsaved-banner has-close nav-font12 px-4 py-2" role="status">
      <i class="fas fa-bookmark mr-1" aria-hidden="true"></i>
      Saved as the template "{{ savedName }}". Rename or edit it from Templates on the reports screen.
      <button type="button" class="banner-close btn btn-sm p-0" aria-label="Dismiss" (click)="templateSaved.set(null)">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  }

  @if (templateSaveFailed()) {
    <div class="unsaved-banner has-close nav-font12 px-4 py-2" role="alert">
      <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
      Couldn't save this deck as a template.
      <button type="button" class="banner-close btn btn-sm p-0" aria-label="Dismiss" (click)="templateSaveFailed.set(false)">
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
    </div>
  }

  @if (staleAfterEdit()) {
    <div class="unsaved-banner nav-font12 px-4 py-2" role="alert">
      <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
      The assistant's change was saved, but this view couldn't be refreshed — reload to see it.
    </div>
  }
  </ng-container>
  }

  @if (loading()) {
    <div class="text-center my-5 grey4">
      <i class="fas fa-spinner fa-spin fa-2x" role="img" aria-label="Loading report"></i>
    </div>
  } @else if (loadFailed()) {
    <div class="text-center my-5 grey4 nav-font14">
      Couldn't load this {{ isTemplate() ? 'template' : 'report' }}.
    </div>
  } @else if (design(); as reportDesign) {
    <!-- Save state, and the two "this deck isn't quite what it should be" notes. All of it is
         Navigator talking to itself: a client is shown the deck that exists, not a list of what it
         was supposed to be, and the blocks those missing questions would have filled are left out of
         their copy entirely (the share dialog is where that gets said, to someone who can act). -->
    @if (!thumbnail) {
    <ng-container *internalOnly>
    @if (!presenting()) {
      @if (designSaveFailure(); as failure) {
        <div class="unsaved-banner nav-font12 px-4 py-2" role="alert">
          <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
          @switch (failure) {
            @case ('VersionConflict') {
              This report was changed by someone else while you were editing. Reloading takes their
              version and discards your unsaved changes.
              <button type="button" class="btn btn-link btn-sm nav-font12 p-0 ml-1 align-baseline"
                      [disabled]="savingDesign()" (click)="reloadDesign()">Reload</button>
            }
            @case ('Busy') {
              Another save for this report is still finishing.
              <button type="button" class="btn btn-link btn-sm nav-font12 p-0 ml-1 align-baseline"
                      [disabled]="savingDesign()" (click)="retryDesignSave()">Try again</button>
            }
            @case ('Invalid') {
              These changes couldn't be saved because the report structure isn't valid. Nothing was
              written — please report this.
            }
            @case ('ArchiveFailed') {
              Nothing was saved: the previous version couldn't be backed up first, so it was left
              untouched. Try again shortly.
            }
            @default {
              Couldn't save these changes.
              <button type="button" class="btn btn-link btn-sm nav-font12 p-0 ml-1 align-baseline"
                      [disabled]="savingDesign()" (click)="retryDesignSave()">Try again</button>
            }
          }
        </div>
      } @else if (savingDesign()) {
        <div class="unsaved-banner nav-font12 px-4 py-2" role="status">
          <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>
          Saving changes…
        </div>
      } @else if (previewingSettings() && unsaved()) {
        <div class="unsaved-banner nav-font12 px-4 py-2">
          <i class="fas fa-eye mr-1" aria-hidden="true"></i>
          Previewing changes to the report settings — Save in the panel to keep them, or Cancel to put them back.
        </div>
      } @else if (unsaved()) {
        <div class="unsaved-banner nav-font12 px-4 py-2">
          <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
          These changes aren't saved yet — choose Save changes to keep them, or Cancel to discard.
        </div>
      }
    }
    <!-- The deck maps questions its data doesn't have, whatever put it that way -- a source switch,
         a refresh that dropped a question, a design built against another survey. Stated for the
         whole deck rather than left to the affected blocks, and hidden while presenting for the same
         reason as the template note below. -->
    @if (!presenting() && unresolvedVariables().length && !isNoteDismissed(unresolvedVariablesNote())) {
      <div class="unresolved-banner has-close nav-font12 px-4 py-2">
        <button type="button" class="banner-close btn btn-sm p-0" aria-label="Dismiss" (click)="dismissNote(unresolvedVariablesNote())">
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
        <button type="button"
                class="btn btn-link btn-sm nav-font12 p-0 text-left"
                [attr.aria-expanded]="unresolvedVariablesOpen()"
                aria-controls="unresolvedVariablesList"
                (click)="unresolvedVariablesOpen.set(!unresolvedVariablesOpen())">
          <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>
          {{ unresolvedVariables().length }}
          question{{ unresolvedVariables().length === 1 ? '' : 's' }} in this report
          {{ unresolvedVariables().length === 1 ? "isn't" : "aren't" }} in
          {{ dataSourceName() || 'the data it is reading' }} — those charts will say they have no data
          <i class="fas ml-1" [class.fa-chevron-down]="!unresolvedVariablesOpen()"
             [class.fa-chevron-up]="unresolvedVariablesOpen()" aria-hidden="true"></i>
        </button>

        @if (unresolvedVariablesOpen()) {
          <ul id="unresolvedVariablesList" class="list-unstyled mb-0 mt-2">
            @for (key of unresolvedVariables(); track key) {
              <li class="mb-1"><span class="font-weight-bold">{{ key }}</span></li>
            }
          </ul>
          <p class="grey4 mb-0 mt-1">
            Switch to data that has them, or edit the charts to use questions this data does have.
          </p>
        }
      </div>
    }

    <!-- Built from a template, and the survey didn't have everything it asked for. Navigator's own
         note, hidden while presenting: a client should see the deck that exists, not a list of what
         it was supposed to be. -->
    @if (!presenting() && unresolvedSlots().length && !isNoteDismissed(unresolvedSlotsNote())) {
      <div class="unresolved-banner has-close nav-font12 px-4 py-2">
        <button type="button" class="banner-close btn btn-sm p-0" aria-label="Dismiss" (click)="dismissNote(unresolvedSlotsNote())">
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
        <button type="button"
                class="btn btn-link btn-sm nav-font12 p-0 text-left"
                [attr.aria-expanded]="unresolvedOpen()"
                aria-controls="unresolvedList"
                (click)="unresolvedOpen.set(!unresolvedOpen())">
          <i class="fas fa-circle-info mr-1" aria-hidden="true"></i>
          {{ unresolvedSlots().length }} thing{{ unresolvedSlots().length === 1 ? '' : 's' }} this
          template asked for {{ unresolvedSlots().length === 1 ? "isn't" : "aren't" }} in this survey
          <i class="fas ml-1" [class.fa-chevron-down]="!unresolvedOpen()"
             [class.fa-chevron-up]="unresolvedOpen()" aria-hidden="true"></i>
        </button>

        @if (unresolvedOpen()) {
          <ul id="unresolvedList" class="list-unstyled mb-0 mt-2">
            @for (entry of unresolvedSlots(); track entry.slot) {
              <li class="d-flex align-items-start mb-1">
                <span>
                  <span class="font-weight-bold">{{ entry.slot }}</span>
                  @if (entry.why) {
                    <span class="grey4"> — {{ entry.why }}</span>
                  }
                </span>
                <button type="button"
                        class="btn btn-link btn-sm nav-font12 p-0 ml-auto"
                        [disabled]="editing() || savingDesign()"
                        [tooltip]="editing() ? 'Finish editing first' : 'Remove this from the list'"
                        container="body"
                        [attr.aria-label]="'Mark ' + entry.slot + ' as handled'"
                        (click)="dismissUnresolved(entry.slot)">
                  Handled
                </button>
              </li>
            }
          </ul>
        }
      </div>
    }
    </ng-container>
    }

    <div class="deck-body"
         [class.is-editing]="editing()"
         [class.with-editor]="moduleRailOpen()"
         [class.is-resizing]="resizingRail()"
         [class.nav-collapsed]="navCollapsed()"
         [style.--edit-panel-width.px]="editPanelWidth()">
      <!-- Section / subsection nav -->
      @if (!thumbnail) {
      @if (navCollapsed()) {
        <!-- The index folded away on a narrow window: a strip holding the way back to it, and how
             many filters are set, since those are behind it too. -->
        <div class="deck-nav-collapsed">
          <button id="deck-nav-expand" type="button" class="deck-nav-toggle"
                  aria-label="Show the report index"
                  title="Show the report index"
                  (click)="setNavCollapsed(false)">
            <i class="fas fa-angles-right" aria-hidden="true"></i>
          </button>
          @if (filtersInNav() && activeChips().length) {
            <span class="deck-nav-tab-count mt-2">{{ activeChips().length }}<span class="sr-only"> filters active</span></span>
          }
        </div>
      } @else {
      <nav #deckNav class="deck-nav py-3" aria-label="Report subsections"
           [class.is-dragging]="!!subSectionDrag()"
           (dragover)="onNavDragOver($event)"
           (drop)="onNavDrop($event)">
        <!-- On a window too narrow for the filter rail, the filters become a second tab here rather
             than going missing. Viewing only: edit mode has no filters to show. -->
        @if (narrowLayout()) {
          <div class="deck-nav-tabs d-flex">
            @if (filtersInNav()) {
              <div class="d-flex flex-fill" role="group" aria-label="Show in this panel">
                <button type="button" class="deck-nav-tab flex-fill"
                        [class.active]="navTab() === 'sections'"
                        [attr.aria-pressed]="navTab() === 'sections'"
                        (click)="navTab.set('sections')">
                  Sections
                </button>
                <button type="button" class="deck-nav-tab flex-fill"
                        [class.active]="navTab() === 'filters'"
                        [attr.aria-pressed]="navTab() === 'filters'"
                        (click)="navTab.set('filters')">
                  Filters
                  @if (activeChips().length) {
                    <span class="deck-nav-tab-count ml-1">{{ activeChips().length }}<span class="sr-only"> active</span></span>
                  }
                </button>
              </div>
            } @else {
              <span class="flex-fill"></span>
            }
            <button id="deck-nav-collapse" type="button" class="deck-nav-toggle"
                    aria-label="Hide the report index"
                    title="Hide the report index"
                    (click)="setNavCollapsed(true)">
              <i class="fas fa-angles-left" aria-hidden="true"></i>
            </button>
          </div>
        }

        @if (filtersInNav() && navTab() === 'filters') {
          <div class="deck-rail deck-nav-filters p-3">
            <ng-container [ngTemplateOutlet]="filterControls"></ng-container>
          </div>
        } @else {
        @if (editing()) {
          <p id="subsection-grip-help" class="sr-only">
            Hold Alt and press the up or down arrow to move this subsection. At the top or bottom of its
            section, it moves into the section above or below.
          </p>
        }
        <!-- Jump to a subsection by name. Sticky, so it stays reachable on a deck long enough to need it.
             Hidden while presenting, where the nav is not shown at all. -->
        @if (!presenting()) {
          <div class="deck-nav-search">
            <input type="search"
                   class="deck-nav-search-input w-100"
                   placeholder="Jump to a section"
                   aria-label="Find a section by name"
                   [ngModel]="navQuery()"
                   (ngModelChange)="navQuery.set($event)">
          </div>
        }

        <!-- Directly above the first section, so it reads as adding one of the things under it -- a
             section is the group heading it sits on, which is what tells someone what it adds. A
             section arrives with one blank subsection, since a section cannot be empty; it lands at
             the end of the index, which is scrolled to for it. -->
        @if (editing()) {
          <button type="button" class="btn btn-sm deck-nav-add-section ml-auto mr-2 mt-2"
                  (click)="addSection()">
            <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add section
          </button>
        }

        <!-- An empty report and a search that found nothing are different things to be told. -->
        @if (!navSubSections().length) {
          <p class="deck-nav-empty px-3 mb-0">No sections yet.</p>
        } @else if (!visibleNavSubSections().length) {
          <p class="deck-nav-empty px-3 mb-0">No subsections match "{{ navQuery() }}".</p>
        }

        @for (nav of visibleNavSubSections(); track nav.subSection.id; let i = $index) {
          @if (startsSection(i)) {
            <!-- A section's name, which a dragged subsection can be dropped on to go to the top of it.
                 Everything else done to a section is in its ⋯ menu: adding a subsection, moving the
                 section. One quiet control rather than three icons on every heading, since moving a
                 section is rare -- and the menu is on the name's own row, so it takes no extra line. -->
            @if (editing()) {
              <div class="d-flex align-items-center section-row mt-3 mb-1" [attr.data-nav-heading]="nav.sectionId">
              <input type="text"
                       class="inline-edit deck-nav-group text-uppercase flex-fill"
                     [attr.aria-label]="'Section name: ' + nav.sectionName"
                     [value]="nav.sectionName"
                     (change)="renameSection(nav.sectionId, $any($event.target).value)">
                <!-- On the body rather than in the index: the index scrolls, and a menu inside it would
                     be cut off at its edge. Opening rightwards from the ⋯, out over the deck: the index is
                     against the window's left edge, so a menu aligned to the right of the button ran off it.
                     .ecr-theme on the menu itself, because on the body it is outside the deck: without it, it
                     took Navigator's font rather than the deck's, unlike the Edit and Actions menus. -->
                <div class="btn-group ml-1" dropdown container="body" #sectionMenu="bs-dropdown">
                  <button dropdownToggle type="button" class="subsection-action section-menu-toggle btn btn-sm p-0"
                          aria-haspopup="menu"
                          [attr.aria-controls]="'section-menu-' + nav.sectionId"
                          [attr.aria-expanded]="sectionMenu.isOpen"
                          [attr.aria-label]="'Options for section ' + nav.sectionName">
                    <i class="fas fa-ellipsis" aria-hidden="true"></i>
                  </button>
                  <ul [id]="'section-menu-' + nav.sectionId" *dropdownMenu
                      class="dropdown-menu section-menu ecr-theme" role="menu">
                    <li role="menuitem">
                      <button type="button" class="dropdown-item" (click)="addSubSection(nav.sectionId)">
                        <i class="fas fa-plus mr-2" aria-hidden="true"></i>Add subsection
                      </button>
                    </li>
                    <li role="menuitem">
                      <button type="button" class="dropdown-item"
                              [disabled]="!canMoveSection(nav.sectionId, -1)"
                              (click)="moveSection(nav.sectionId, -1)">
                        <i class="fas fa-arrow-up mr-2" aria-hidden="true"></i>Move up
                      </button>
                    </li>
                    <li role="menuitem">
                      <button type="button" class="dropdown-item"
                              [disabled]="!canMoveSection(nav.sectionId, 1)"
                              (click)="moveSection(nav.sectionId, 1)">
                        <i class="fas fa-arrow-down mr-2" aria-hidden="true"></i>Move down
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            } @else {
              <p class="deck-nav-group text-uppercase mt-3 mb-1">{{ nav.sectionName }}</p>
            }
          }
          <!-- Where a dragged subsection would land: an empty box in front of the subsection it would
               go before. Not a [data-nav-row], so the drag's measuring of the rows passes over it. -->
          @if (subSectionDropsBefore(nav.subSection.id)) {
            <div class="nav-drop-slot" aria-hidden="true"></div>
          }
          <!-- While a module is dragged, every subsection it can go to is outlined, and dropping it on
               one moves it there. -->
          <!-- In edit mode the whole row is what is dragged, name and all, within its section or into
               another. -->
          <div class="d-flex align-items-center subsection-row"
               [attr.data-nav-row]="nav.subSection.id"
               [attr.draggable]="editing() ? 'true' : null"
               [class.is-draggable]="editing()"
               [class.is-dragged]="subSectionDrag() === nav.subSection.id"
               [attr.data-nav-section]="nav.sectionId"
               (dragstart)="startSubSectionDrag($event, nav.subSection)"
               (dragend)="endSubSectionDrag()"
               [class.can-drop]="canDropOn(nav.subSection.id)"
               [class.is-drop-target]="dropSubSectionId() === nav.subSection.id"
               (dragover)="onSubSectionDragOver($event, nav.subSection.id)"
               (dragleave)="onSubSectionDragLeave(nav.subSection.id)"
               (drop)="onSubSectionDrop($event, nav.subSection.id)">
            <button type="button"
                    class="subsection-link-btn flex-fill text-left px-3 py-1"
                    [class.active]="nav.subSection.id === selectedSubSectionId()"
                    [attr.aria-current]="nav.subSection.id === selectedSubSectionId() ? 'page' : null"
                    [attr.data-subsection-link]="nav.subSection.id"
                    [attr.aria-describedby]="editing() ? 'subsection-grip-help' : null"
                    (keydown.alt.arrowup)="editing() ? moveSubSectionByKey(nav.subSection.id, -1) : null"
                    (keydown.alt.arrowdown)="editing() ? moveSubSectionByKey(nav.subSection.id, 1) : null"
                    (click)="selectSubSection(nav.subSection.id)">
              {{ nav.subSection.name }}
            </button>
          </div>
          <!-- Or under the last of a section, where it would go at the end of it. -->
          @if (endsSection(i) && subSectionDropsAtEnd(nav.sectionId)) {
            <div class="nav-drop-slot" aria-hidden="true"></div>
        }
        }
        }

        <!-- The index's foot, pinned to the bottom of it so it stays in reach however long the index
             runs. Navigator users only: a share link has nowhere to go back to, and cannot edit. -->
        <div *internalOnly class="deck-nav-foot">
          <!-- The way back, here rather than above the deck name, where it took a line off the top
               of every page. -->
          <a class="deck-nav-back nav-font12 align-self-start" [routerLink]="backLink">
          <i class="fas fa-arrow-left mr-1" aria-hidden="true"></i>{{ isTemplate() ? 'Back to templates' : 'Back to reports' }}
        </a>
        </div>
      </nav>
      }
      }

      <!-- Selected subsection -->
      <main #deckMain class="deck-main"
            [class.is-dragging]="!!dragging()"
            (dragover)="onDeckDragOver($event)"
            (drop)="onDeckDrop($event)">
        <p class="sr-only" aria-live="polite">{{ moveAnnouncement() }}</p>
        @if (editing()) {
          <p id="block-grip-help" class="sr-only">
            Use the up and down arrow keys to move this module on the page, and on a half-width module
            the left and right arrow keys to put it on that side. To move it to another subsection,
            open its settings and change Subsection.
          </p>
        }
        @if (selectedSubSection(); as subSection) {
          <!-- The report's name on the right, as a running head: on a slide, or a page someone has
               printed, it says which report this is without the top bar. The name as being typed
               while editing, so the two never disagree. Not on a thumbnail: its card names it. -->
          <div class="subsection-header d-flex align-items-center px-3 py-3 mb-3">
            <div class="subsection-title flex-fill">
            @if (editing()) {
              <input type="text"
                     class="inline-edit on-dark subsection-name nav-font20 font-weight-bold w-100"
                     maxlength="100"
                     aria-label="Subsection name"
                     [ngModel]="subSection.name"
                     (ngModelChange)="renameSubSection(subSection.id, $event)">
              <input type="text"
                     class="inline-edit on-dark nav-font12 w-100 mt-1"
                     maxlength="200"
                     placeholder="Subheader (optional)"
                     aria-label="Subsection subheader"
                     [ngModel]="subSection.subheader ?? ''"
                     (ngModelChange)="setSubSectionSubheader(subSection.id, $event)">
            } @else {
              <h2 class="nav-font20 font-weight-bold mb-0">{{ subSection.name }}</h2>
              @if (subSection.subheader) {
                <p class="nav-font12 mb-0">{{ subSection.subheader }}</p>
              }
            }
            </div>
            @if (!thumbnail && (editing() ? editedName() : deckName()).trim(); as reportName) {
              <span class="subsection-report-name ml-3">{{ reportName }}</span>
            }
          </div>

          <!-- The subsection's "so what", above the charts that argue it. Shown when it has been written,
               or whenever editing, so there is somewhere to write it. -->
          @if (subSection.keyTakeaway || editing()) {
            <div class="key-takeaway d-flex align-items-baseline px-3 py-2 mb-3">
              <span class="key-takeaway-label mr-2">Key takeaway</span>
              @if (editing()) {
                <input type="text"
                       class="inline-edit key-takeaway-input flex-fill"
                       maxlength="240"
                       placeholder="The one thing this subsection says (optional)"
                       aria-label="Key takeaway"
                       [ngModel]="subSection.keyTakeaway ?? ''"
                       (ngModelChange)="setSubSectionKeyTakeaway(subSection.id, $event)">
              } @else {
                <p class="key-takeaway-text mb-0">{{ subSection.keyTakeaway }}</p>
              }
            </div>
          }

          @if (hasActiveFilters()) {
            <div class="d-flex flex-wrap align-items-center mb-3">
              @for (chip of activeChips(); track chip.key) {
                <span class="filter-chip mr-2 mb-1">
                  {{ chip.label }}
                  @if (!presenting()) {
                    <button type="button"
                            class="chip-x bg-transparent border-0 p-0 ml-1"
                            [attr.aria-label]="'Remove filter ' + chip.label"
                            (click)="clearFilter(chip.key)">
                      <i class="fas fa-close" aria-hidden="true"></i>
                    </button>
                  }
                </span>
              }
              @if (!presenting()) {
                <button type="button" class="btn btn-link btn-sm nav-font12 p-0 ml-1" (click)="resetFilters()">
                  Clear all
                </button>
              }
            </div>
          }

          @if (comparing()) {
            <div class="compare-grid"
                 [style.grid-template-columns]="'repeat(' + compareColumns().length + ', minmax(340px, 1fr))'">
              @for (column of compareColumns(); track column.label) {
                <h3 class="compare-heading mb-0">
                  {{ column.label }}
                  <span class="compare-size font-weight-normal ml-2">n = {{ column.size }}</span>
                </h3>
              }
              @for (row of compareRows(); track row.block.id) {
                @if (row.spans) {
                  <!-- Describes the study, not a subgroup, so it stays whole across the columns. -->
                  <div class="compare-span">
                    <app-report-block-host
                      [block]="row.block"
                      (click)="editing() ? openBlockEditor(editTargetOf(row.block)) : null"
                      (focusin)="editing() ? openBlockEditor(editTargetOf(row.block)) : null"
                      [selected]="editedBlockId() === editTargetOf(row.block)"
                      [contentDerived]="contentDerived(row.block)"
                      [series]="row.cells[0].series"
                      [palette]="reportDesign.colorPalette"
                      [studyFacts]="facts()"
                      [editing]="editing()"
                      (headerChange)="patchBlock(row.block.id, { header: $event })"
                      (blockPatch)="patchBlock(row.block.id, $event)">
                      @if (editing()) {
                        <app-report-block-actions blockActions
                          [label]="blockLabel(row.block)"
                          [canMoveUp]="blockIndexOf(row.block.id) > 0"
                          [canMoveDown]="blockIndexOf(row.block.id) < blockCount() - 1"
                          [blockId]="row.block.id"
                          (move)="moveBlockByKey(row.block.id, $event)"
                          (dragStart)="startBlockDrag($event, row.block)"
                          (dragEnd)="endBlockDrag()"
                          [half]="row.block.segmentLocation !== 'full'"
                          (sideMove)="moveBlockToSide(row.block.id, $event)">
                        </app-report-block-actions>
                      }
                    </app-report-block-host>
                  </div>
                } @else {
                  @for (cell of row.cells; track $index; let first = $first) {
                    <!-- The editing chrome goes on the first column only. A breakout renders the same
                         block once per subgroup, so projecting it into every cell would put four pens
                         on one chart -- and they would all edit the same block anyway. -->
                    <app-report-block-host
                      [block]="row.block"
                      (click)="editing() ? openBlockEditor(editTargetOf(row.block)) : null"
                      (focusin)="editing() ? openBlockEditor(editTargetOf(row.block)) : null"
                      [selected]="editedBlockId() === editTargetOf(row.block)"
                      [contentDerived]="contentDerived(row.block)"
                      [series]="cell.series"
                      [palette]="reportDesign.colorPalette"
                      [alignCount]="row.alignCount"
                      [compact]="true"
                      [editing]="editing() && first"
                      (headerChange)="patchBlock(row.block.id, { header: $event })"
                      (blockPatch)="patchBlock(row.block.id, $event)">
                      @if (editing() && first) {
                        <app-report-block-actions blockActions
                          [label]="blockLabel(row.block)"
                          [canMoveUp]="blockIndexOf(row.block.id) > 0"
                          [canMoveDown]="blockIndexOf(row.block.id) < blockCount() - 1"
                          [blockId]="row.block.id"
                          (move)="moveBlockByKey(row.block.id, $event)"
                          (dragStart)="startBlockDrag($event, row.block)"
                          (dragEnd)="endBlockDrag()"
                          [half]="row.block.segmentLocation !== 'full'"
                          (sideMove)="moveBlockToSide(row.block.id, $event)">
                        </app-report-block-actions>
                      }
                    </app-report-block-host>
                  }
                }
              }
            </div>
          } @else {
          <!-- Where the last module was removed. Edit mode only: a reader of a subsection nobody
               filled in sees its heading and nothing more, rather than an instruction. -->
          @if (editing() && !renderedRows().length) {
            <p class="empty-page-note nav-font12 px-3 py-4 mb-3">
              Nothing on this page yet. Use Add module, under the panel on the right.
            </p>
          }
          @for (row of renderedRows(); track $index) {
            <!-- Where a drag would land the card: an empty box opening in front of the row it would
                 go before, an outline over the half of a row it would go into, or one round a card it
                 would swap with. Not a .deck-row,
                 so the drag's own measuring of the rows passes over it. -->
            @if (dropsAtGap($index)) {
              <div class="deck-drop-slot" aria-hidden="true"></div>
            }
            <div class="row mb-3 deck-row"
                 [class.drop-left]="dropsAtSide($index, 'left')"
                 [class.drop-right]="dropsAtSide($index, 'right')">
              @if (row.full; as cell) {
                <div class="col-12" [attr.data-drop-block]="cell.block.id" [class.drop-onto]="dropsOnto(cell.block.id)" [class.is-dragged]="isDragged(cell.block.id)" [style.min-width.px]="columnMinWidth(cell)">
                  <app-report-block-host
                    [block]="cell.block"
                    (click)="editing() ? openBlockEditor(editTargetOf(cell.block)) : null"
                    (focusin)="editing() ? openBlockEditor(editTargetOf(cell.block)) : null"
                    [selected]="editedBlockId() === editTargetOf(cell.block)"
                    [contentDerived]="contentDerived(cell.block)"
                    [series]="cell.series"
                    [explore]="cell.explore"
                    [palette]="reportDesign.colorPalette"
                    [editing]="editing()"
                    [studyFacts]="facts()"
                    (headerChange)="patchBlock(cell.block.id, { header: $event })"
                    (blockPatch)="patchBlock(cell.block.id, $event)">
                    @if (editing()) {
                      <app-report-block-actions blockActions
                        [label]="blockLabel(cell.block)"
                        [canMoveUp]="blockIndexOf(cell.block.id) > 0"
                        [canMoveDown]="blockIndexOf(cell.block.id) < blockCount() - 1"
                        [blockId]="cell.block.id"
                        (move)="moveBlockByKey(cell.block.id, $event)"
                        (dragStart)="startBlockDrag($event, cell.block)"
                        (dragEnd)="endBlockDrag()"
                        [half]="cell.block.segmentLocation !== 'full'"
                        (sideMove)="moveBlockToSide(cell.block.id, $event)">
                      </app-report-block-actions>
                    }
                  </app-report-block-host>
                </div>
              }
              @if (row.left; as cell) {
                <div class="col-6" [attr.data-drop-block]="cell.block.id" [class.drop-onto]="dropsOnto(cell.block.id)" [class.is-dragged]="isDragged(cell.block.id)" [style.min-width.px]="columnMinWidth(cell)">
                  <app-report-block-host
                    [block]="cell.block"
                    (click)="editing() ? openBlockEditor(editTargetOf(cell.block)) : null"
                    (focusin)="editing() ? openBlockEditor(editTargetOf(cell.block)) : null"
                    [selected]="editedBlockId() === editTargetOf(cell.block)"
                    [contentDerived]="contentDerived(cell.block)"
                    [series]="cell.series"
                    [explore]="cell.explore"
                    [palette]="reportDesign.colorPalette"
                    [editing]="editing()"
                    [studyFacts]="facts()"
                    (headerChange)="patchBlock(cell.block.id, { header: $event })"
                    (blockPatch)="patchBlock(cell.block.id, $event)">
                    @if (editing()) {
                      <app-report-block-actions blockActions
                        [label]="blockLabel(cell.block)"
                        [canMoveUp]="blockIndexOf(cell.block.id) > 0"
                        [canMoveDown]="blockIndexOf(cell.block.id) < blockCount() - 1"
                        [blockId]="cell.block.id"
                        (move)="moveBlockByKey(cell.block.id, $event)"
                        (dragStart)="startBlockDrag($event, cell.block)"
                        (dragEnd)="endBlockDrag()"
                        [half]="cell.block.segmentLocation !== 'full'"
                        (sideMove)="moveBlockToSide(cell.block.id, $event)">
                      </app-report-block-actions>
                    }
                  </app-report-block-host>
                </div>
              }
              @if (row.right; as cell) {
                <!-- Pushed across where there is no left half, so a right half alone stays on the
                     right rather than sliding into the empty slot. -->
                <div class="col-6" [class.offset-6]="!row.left" [attr.data-drop-block]="cell.block.id" [class.drop-onto]="dropsOnto(cell.block.id)" [class.is-dragged]="isDragged(cell.block.id)"
                     [style.min-width.px]="columnMinWidth(cell)">
                  <app-report-block-host
                    [block]="cell.block"
                    (click)="editing() ? openBlockEditor(editTargetOf(cell.block)) : null"
                    (focusin)="editing() ? openBlockEditor(editTargetOf(cell.block)) : null"
                    [selected]="editedBlockId() === editTargetOf(cell.block)"
                    [contentDerived]="contentDerived(cell.block)"
                    [series]="cell.series"
                    [explore]="cell.explore"
                    [palette]="reportDesign.colorPalette"
                    [editing]="editing()"
                    [studyFacts]="facts()"
                    (headerChange)="patchBlock(cell.block.id, { header: $event })"
                    (blockPatch)="patchBlock(cell.block.id, $event)">
                    @if (editing()) {
                      <app-report-block-actions blockActions
                        [label]="blockLabel(cell.block)"
                        [canMoveUp]="blockIndexOf(cell.block.id) > 0"
                        [canMoveDown]="blockIndexOf(cell.block.id) < blockCount() - 1"
                        [blockId]="cell.block.id"
                        (move)="moveBlockByKey(cell.block.id, $event)"
                        (dragStart)="startBlockDrag($event, cell.block)"
                        (dragEnd)="endBlockDrag()"
                        [half]="cell.block.segmentLocation !== 'full'"
                        (sideMove)="moveBlockToSide(cell.block.id, $event)">
                      </app-report-block-actions>
                    }
                  </app-report-block-host>
                </div>
              }
            </div>
          }
          @if (dropsAtGap(renderedRows().length)) {
            <div class="deck-drop-slot" aria-hidden="true"></div>
          }

          }

          <!-- Only where there is somewhere to go. A subsection that has never used pages is a
               single page, so this is absent on every deck that predates them. -->
          @if (pageCount() > 1 && !thumbnail) {
            <!-- Room under the last card as tall as the bar, which sits over the content while it
                 scrolls: without it the foot of a card just added at the bottom is under the bar. -->
            <div class="page-controller-clearance" aria-hidden="true"></div>
            <nav class="page-controller d-flex align-items-center justify-content-center"
                 aria-label="Pages of this subsection">
              <button type="button" class="page-step btn btn-sm p-0"
                      [disabled]="pageIndex() === 0"
                      aria-label="Previous page"
                      (click)="previousPage()">
                <i class="fas fa-chevron-left" aria-hidden="true"></i>
              </button>
              <span class="page-count mx-3" aria-live="polite">
                Page {{ pageIndex() + 1 }} of {{ pageCount() }}
              </span>
              <button type="button" class="page-step btn btn-sm p-0"
                      [disabled]="pageIndex() === pageCount() - 1"
                      aria-label="Next page"
                      (click)="nextPage()">
                <i class="fas fa-chevron-right" aria-hidden="true"></i>
              </button>
            </nav>
          }

          @if (presenting()) {
            <p class="present-tip text-center mb-0">
              ← / → to move between pages · Esc to exit present mode
            </p>
          }
        } @else if (!navSubSections().length) {
          <!-- A report with nothing in it says so, and says what to do first, rather than leaving a
               blank page and controls that have nowhere to put anything. -->
          <div class="empty-report text-center px-3">
            <i class="fas fa-layer-group empty-report-icon mb-3" aria-hidden="true"></i>
            <h2 class="empty-report-title mb-2">This report is empty</h2>
            @if (editing()) {
              <p class="empty-report-text mb-3">
                Start with a section. It comes with a subsection, ready for its first module.
              </p>
              <button type="button" class="btn btn-sm deck-btn is-tool is-solid shadow-none"
                      (click)="addSection()">
                <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add a section
              </button>
            } @else {
              <p class="empty-report-text mb-0">Nothing has been added to this report yet.</p>
              <ng-container *internalOnly>
                @if (!isTemplate()) {
                  <p class="empty-report-text mt-1 mb-0">Choose Edit at the top to start building it.</p>
                }
              </ng-container>
            }
          </div>
        }
      </main>

      <!-- Filter rail. Not in edit mode: the right-hand side is the module panel's there, and two
           columns of controls beside the deck left the charts too narrow to judge. Any filter set
           beforehand still applies, and its chip above the deck says so and can clear it. On a
           narrow window the same controls are a tab of the index instead. -->
      @if (!editing() && !thumbnail && !filtersInNav()) {
      <aside class="deck-rail p-3" aria-label="Report controls">
        <ng-container [ngTemplateOutlet]="filterControls"></ng-container>
      </aside>
      }

      <ng-template #filterControls>
        @if (reportDesign.filters.compareBy.length) {
          <h2 class="font-weight-bold mb-2">Compare by</h2>
          <or-report-select class="mb-3"
                            ariaLabel="Compare by"
                            emptyLabel="None"
                            placeholder="None"
                            [options]="compareByOptions()"
                            [value]="compareBy()"
                            (selectedChange)="setCompareBy($event)">
          </or-report-select>
        }

        <div class="d-flex align-items-center">
          <h2 class="font-weight-bold mb-0">Filters</h2>
          <button type="button"
                  class="btn btn-link btn-sm nav-font12 p-0 ml-auto"
                  [disabled]="!hasActiveFilters()"
                  (click)="resetFilters()">
            Reset
          </button>
        </div>
        <p class="grey4 nav-font12 mb-3">n = {{ baseN() }} of {{ totalN() }}</p>

        @for (row of filterRows(); track row.key) {
          <div class="mb-2">
            <label [attr.for]="'filter-' + row.key" class="deck-rail-label mb-1">{{ variableName(row.key) }}</label>
            <or-report-select [id]="'filter-' + row.key"
                              emptyLabel="All"
                              placeholder="All"
                              [options]="row.selectOptions"
                              [value]="activeFilters()[row.key] || ''"
                              (selectedChange)="setFilter(row.key, $event)">
            </or-report-select>
          </div>
        }
      </ng-template>

      <!-- AI chat edit, as a window floating over the bottom right of the deck rather than a rail
           beside it: it changes the deck, so it should take as little of the deck as it can. Moved
           by dragging its bar, and shrunk to a pill when it is in the way. The pill always sits in the
           bottom right, and the window opens from it there again, wherever it was dragged before.
           Hidden rather than removed while it is a pill, so the conversation is still there. -->
      @if (chatAvailable()) {
        <aside #chatWindow class="chat-window" aria-label="Edit with AI"
               [class.is-minimized]="chatMinimized()"
               [class.is-placed]="!!chatPosition() && !chatMinimized()"
               [style.left.px]="chatMinimized() ? null : chatPosition()?.left"
               [style.top.px]="chatMinimized() ? null : chatPosition()?.top">
          @if (chatMinimized()) {
            <button type="button" class="chat-pill" (click)="openChat()">
              <i class="fas fa-wand-magic-sparkles mr-2" aria-hidden="true"></i>Edit with AI
            </button>
          }
          <div class="chat-window-inner" [hidden]="chatMinimized()">
            <div class="chat-window-bar d-flex align-items-center"
                 (pointerdown)="startChatDrag($event)"
                 (pointermove)="dragChat($event)"
                 (pointerup)="endChatDrag($event)"
                 (pointercancel)="endChatDrag($event)">
              <i class="fas fa-grip-dots-vertical chat-window-grip mr-2" aria-hidden="true"></i>
              <h2 class="nav-secondary nav-font14 font-weight-bold mb-0">Edit with AI</h2>
              <!-- Minimise only, never close: the chat is always on the deck, as this or a pill. -->
              <button type="button" class="chat-window-action ml-auto"
                      aria-label="Minimise Edit with AI"
                      (click)="minimizeChat()">
                <i class="fas fa-minus" aria-hidden="true"></i>
              </button>
            </div>
            <div class="chat-window-body">
          <or-report-chat [reportId]="reportId"
                          [baseVersion]="baseVersion()"
                          [viewing]="chatViewing()"
                          [disabled]="editing()"
                              [showHeader]="false"
                          (designChanged)="onChatEdit($event)"
                              (closed)="minimizeChat()"></or-report-chat>
            </div>
          </div>
        </aside>
      }

      <!-- The edit panel: there for the whole of edit mode. With no module open it holds the
           subsection's own settings and a prompt to open one; with one open, that module's settings.
           Adding and removing modules sit in its foot either way. The same track as the chat and
           the history and never beside them, since all three change the deck; and one editor for
           the whole deck rather than one per card, handed whichever card is open. -->
      @if (moduleRailOpen()) {
        <aside id="module-settings"
               class="deck-editor"
               [class.is-empty]="!editedBlock()"
               aria-label="Module settings">
          <!-- Wider for a complicated table -- long question names, a banner of many answers, the
               weighting targets -- and back to the usual width on a double-click. -->
          <div class="deck-editor-resize"
               role="separator"
               tabindex="0"
               aria-orientation="vertical"
               aria-label="Resize the settings panel"
               aria-controls="module-settings"
               [attr.aria-valuenow]="editPanelWidth()"
               [attr.aria-valuemin]="railMin"
               [attr.aria-valuemax]="railMax"
               (pointerdown)="startRailResize($event)"
               (dblclick)="setRailWidth(railDefault)"
               (keydown.arrowleft)="$event.preventDefault(); setRailWidth(editPanelWidth() + 24)"
               (keydown.arrowright)="$event.preventDefault(); setRailWidth(editPanelWidth() - 24)"
               (keydown.home)="$event.preventDefault(); setRailWidth(railMin)"
               (keydown.end)="$event.preventDefault(); setRailWidth(railMax)"></div>
          @if (editedBlock(); as edited) {
            <app-report-block-editor
              [block]="edited.block"
              [explainerPlacement]="explainerPlacementOf(edited.block)"
              [series]="edited.series"
              [variables]="pickableVariables()"
              [optionsOf]="answersOf"
              [layoutOf]="answerOptionsOf"
              [contentDerived]="contentDerived(edited.block)"
              [statistics]="design()?.statistics"
              [palette]="palette()"
              (patch)="patchBlock(edited.block.id, $event)"
              (variableChange)="setBlockVariable(edited.block.id, $event)"
              (variablesChange)="setBlockVariables(edited.block.id, $event)"
              (groupByChange)="setBlockGroupBy(edited.block.id, $event)"
              (layerChange)="setBlockLayer(edited.block.id, $event)"
              (priorityChange)="setBlockPriority(edited.block.id, $event)"
              (subgroupsChange)="setBlockSubgroups(edited.block.id, $event)"
              (tableFilterChange)="setBlockTableFilter(edited.block.id, $event)"
              (typeChange)="setBlockType(edited.block.id, $event)"
              (widthChange)="setBlockWidth(edited.block.id, $event)"
              (explainerPlacementChange)="setExplainerPlacement(edited.block.id, $event)"
              (openStatistics)="showStatistics()"
              (copyForQuestions)="copyCrosstab(edited.block.id, $event)"
              (closed)="closeBlockEditor()">
            </app-report-block-editor>
          } @else if (selectedSubSection(); as subSection) {
            <div class="deck-editor-home">
              @if (movedAway(); as moved) {
                <p class="deck-moved-note nav-font12 mb-3">
                  <i class="fas fa-check mr-1" aria-hidden="true"></i>Moved {{ moved.label }} to
                  {{ moved.subSectionName }}.
                  <button type="button" class="btn btn-link btn-sm nav-font12 p-0 align-baseline"
                          (click)="goToMovedBlock()">Go there</button>
                </p>
              }
              <h2 class="deck-editor-title mb-3">This subsection</h2>
              <p class="nav-font12 mb-3">
                To move it, drag its grip in the index, within its section or into another.
              </p>

              <!-- Asks once, where it was pressed, since there is no undo once it is gone. -->
              <div class="d-flex flex-wrap align-items-center">
                @if (confirmingSubSectionDelete()) {
                  <span class="grey5 nav-font12 w-100 mb-2">Delete this subsection and its modules?</span>
                  <!-- The deck's own button, in red: Navigator's error button is sized for a form,
                       and stood twice the height of Keep beside it. -->
                  <button type="button" class="deck-btn deck-btn-danger is-confirm mr-1"
                          (click)="deleteSubSection(subSection.id)">Delete</button>
                  <button type="button" class="deck-btn is-tool bg-transparent"
                          (click)="confirmingSubSectionDelete.set(false)">Keep</button>
                } @else {
                  <button type="button" class="deck-btn deck-btn-danger bg-transparent"
                          [attr.aria-label]="'Delete ' + subSection.name"
                          (click)="confirmingSubSectionDelete.set(true)">
                    <i class="far fa-trash-can mr-1" aria-hidden="true"></i>Delete subsection
                  </button>
                }
              </div>

              <h2 class="deck-editor-title mt-4 mb-2">Modules</h2>
              <p class="nav-font12 mb-0">
                Click a card to change its settings here. Drag it by the
                <i class="fas fa-grip-dots-vertical mx-1" aria-hidden="true"></i><span class="sr-only">grip</span>
                on its left to move it.
              </p>
            </div>
          } @else {
            <!-- Nothing to configure until there is a subsection, so it points at the first step. -->
            <div class="deck-editor-home">
              <h2 class="deck-editor-title mb-2">Nothing to edit yet</h2>
              <p class="nav-font12 mb-0">
                Add a section to start. Its subsection's settings, and its modules', appear here.
              </p>
            </div>
          }

          <div class="deck-editor-foot">
            <!-- The way to move a module without dragging it, by the controls that add and remove one.
                 Lists only where it can go: a matrix, say, not into a subsection that has one. -->
            @if (editedBlock(); as edited) {
              <div class="deck-editor-move">
                <label class="deck-rail-label mb-1" for="module-subsection">Subsection</label>
                <or-report-select id="module-subsection"
                                  [groups]="moveTargetGroups()"
                                  [value]="selectedSubSectionId()"
                                  (selectedChange)="$event !== selectedSubSectionId() ? moveBlockToSubSection(edited.block.id, $event) : null">
                </or-report-select>
              </div>
            }
            <!-- Absent, not disabled, where the block is not the user's to remove: an explainer paired
                 with a matrix goes when the matrix's Explainer is set to none. -->
            @if (editedBlock(); as edited) {
              @if (!contentDerived(edited.block)) {
                <button type="button"
                        class="deck-editor-remove btn btn-sm p-0"
                        (click)="removeBlock(edited.block.id)">
                  <i class="far fa-trash-can mr-1" aria-hidden="true"></i>Remove module
                </button>
              }
            }
            <!-- Navigator's navy, not the deck's palette: this builds the report rather than being
                 part of it, so it should not change colour per client. Only where there is a
                 subsection to add it to. -->
            @if (selectedSubSection()) {
              <button type="button" class="btn btn-sm deck-btn is-tool is-solid shadow-none ml-auto"
                      (click)="addChart()">
                <i class="fas fa-plus mr-1" aria-hidden="true"></i>Add module
              </button>
            }
          </div>
        </aside>
      }
    </div>
  }
</div>
